import { Group } from '@/data/groups/Group';
import { Organization } from '@/data/Organization';
import { CurrentUser } from '@/data/users/CurrentUser';
import { AppPages } from '@/router';
import { KLAYO_COLORS } from '@/themes';
import { IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import { Theme } from 'klayowebshared';
import React, { Component } from 'react';
import { Location } from 'react-router-dom';
import { AppContext } from '../common/AppContext';
import BellIcon from '../resources/images/icons-bell.svg';
import CloseIcon from '../resources/images/icons-close.svg';
import MoreVerticalIcon from '../resources/images/icons-more-vertical.svg';
import Logo from '../resources/images/logo.svg';
import { ExploreKlayoTeaser } from '../views/solution-finder/ExploreKlayoTeaser';
import { isPreviewArmed } from '../views/try/mock/previewMode';
import { AppMenu } from './AppMenu';
import { CurrentUserAvatar } from './CurrentUserAvatar';
import { AppNav } from './AppNav';
import { Button } from './Button';
import { Tooltip } from './Tooltip';

interface AppDrawerProps {
  anchor?: 'left' | 'top' | 'right' | 'bottom';
  transitionDuration?: number;
  drawerWidth?: string;
  open?: boolean;
  onClose?: (e: React.MouseEvent) => void;
  onOpenDevDialog?: (e: React.MouseEvent) => void;
  onNavigation?: (e: React.MouseEvent, callback?: any) => void;
  onOpenDashboard?: (e: React.MouseEvent) => void;
  onSignIn?: (e: React.MouseEvent) => void;
  onSignOut?: (e: React.MouseEvent) => void;
  onMyAccount?: (e: React.MouseEvent) => void;
  onShowNotifications?: (e: React.MouseEvent) => void;
  onShowSwitchUserDialog?: (e: React.MouseEvent) => void;
  onSwitchUser?: (e: React.MouseEvent) => void;
  onDevOptions?: (e: React.MouseEvent) => void;
  onSettings?: (e: React.MouseEvent) => void;
  onDemoSwitch?: (e: React.MouseEvent, demo: any) => void;
  onDemoDataSwitch?: (e: React.MouseEvent) => void;
  onMenuButtonClick?: (e: React.MouseEvent) => void;
  onUserButton?: (e: React.MouseEvent) => void;
  onUserMenuClose?: (e: React.MouseEvent) => void;
  onLogoClick?: (e: React.MouseEvent) => void;
  onOpenSettings?: (e: React.MouseEvent) => void;
  unreadNotifications?: number | null;
  user?: CurrentUser | null;
  organization?: Organization | null;
  theme?: typeof Theme;
  themeName?: string;
  allowDemoSwitch?: boolean;
  allowDemoDataSwitch?: boolean;
  allowSettings?: boolean;
  allowSwitchUser?: boolean;
  allowThemeChange?: boolean;
  location?: Location;
  navItems?: Partial<(typeof AppPages)[keyof typeof AppPages]>[];
  defaultNavItem?: Partial<(typeof AppPages)[keyof typeof AppPages]>;
  getChildNavItems?: (
    page: Partial<(typeof AppPages)[keyof typeof AppPages]>
  ) => Partial<(typeof AppPages)[keyof typeof AppPages]>[] | undefined;
  devMode?: boolean;
  groups?: {
    groups: Group[];
    get: (groupId: string) => Group | undefined;
  };
}

export class AppDrawer extends Component<AppDrawerProps, any> {
  static contextType = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  static defaultProps = {
    anchor: 'left',
    transitionDuration: 200,
    drawerWidth: '276px',
  };

  navRef: any;

  constructor(props: any) {
    super(props);

    this.state = {
      userButton: null,
      unreadNotifications: props.unreadNotifications,
      itemState: {
        collapsed: [],
        childItems: [],
      },
    };

    this.navRef = React.createRef();
  }

  onClose(e: any) {
    const { theme } = this.props;
    const isDesktop = theme.isBreakpointUp('lg');

    if (!isDesktop) this.props.onClose?.(e);
  }

  onOpenDevDialog(e: any) {
    this.onUserMenuClose();
    this.onClose(e);

    const { onOpenDevDialog } = this.props;
    if (onOpenDevDialog) this.onNavigation(e, onOpenDevDialog);
  }

  onNavigation(e: any, callback: any) {
    const { onNavigation } = this.props;
    return onNavigation && onNavigation(e, callback);
  }

  onSignOut(e: any) {
    this.onUserMenuClose();
    this.onClose(e);

    const { onSignOut } = this.props;
    if (onSignOut) this.onNavigation(e, onSignOut);
  }

  onMyAccount(e: any) {
    this.onUserMenuClose();
    this.onClose(e);

    const { onMyAccount } = this.props;
    if (onMyAccount) this.onNavigation(e, onMyAccount);
  }

  onLogoClick(e: any) {
    const { onLogoClick } = this.props;
    if (onLogoClick) this.onNavigation(e, onLogoClick);
  }

  onShowSwitchUserDialog(e: any) {
    this.onUserMenuClose();
    this.onClose(e);

    const { onShowSwitchUserDialog } = this.props;
    if (onShowSwitchUserDialog) this.onNavigation(e, onShowSwitchUserDialog);
  }

  onOpenSettings(e: any) {
    this.onClose(e);
    this.props.onOpenSettings?.(e);
  }

  onDemoSwitch(e: any, demo: any) {
    this.onUserMenuClose();

    const { onDemoSwitch } = this.props;
    if (onDemoSwitch) this.onNavigation(e, (e: any) => onDemoSwitch(e, demo));
  }

  onUserButton(e: any) {
    this.setState({ userButton: e.currentTarget });
  }

  onUserMenuClose() {
    this.setState({ userButton: null });
  }

  onNotificationReceived(notif: any, numUnread: any) {
    this.setUnreadNotifications(numUnread);
  }

  setUnreadNotifications(numUnread: any) {
    this.setState({ unreadNotifications: numUnread });
  }

  updateNav() {
    if (this.navRef && this.navRef.current) this.navRef.current.updateNav();
  }

  render() {
    const {
      user,
      organization,
      theme,
      location,
      transitionDuration,
      navItems,
      defaultNavItem,
      open,
      anchor,
      getChildNavItems,
      onShowNotifications,
      onSwitchUser,
      onDevOptions,
      onSettings,
      onSignOut,
      drawerWidth,
      onNavigation,
      onDemoSwitch,
      allowDemoSwitch,
      onDemoDataSwitch,
      allowDemoDataSwitch,
      allowSwitchUser,
      allowSettings,
      devMode,
    } = this.props;
    const { userButton, unreadNotifications, itemState } = this.state;

    const rootPath = location?.pathname.split('/')[1] || '';
    const currentPage =
      rootPath === '' ? defaultNavItem : navItems?.find((page: any) => page.slug === rootPath);
    const isDesktop = theme.isBreakpointUp('lg');

    return (
      <Drawer
        className='klayo-appdrawer klayo-drawer'
        open={open}
        anchor={anchor}
        hideBackdrop={true}
        variant={isDesktop ? 'permanent' : 'temporary'}
        transitionDuration={isDesktop ? 0 : { enter: transitionDuration, exit: transitionDuration }}
        onClose={this.onClose.bind(this)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { lg: drawerWidth },
            minWidth: { lg: drawerWidth },
            height: { lg: 'auto' },
            bottom: { lg: '0' },
          },
        }}
      >
        <Box
          sx={{
            width: { xs: '100vw', lg: drawerWidth },
            height: { lg: '100%' },
          }}
        >
          <Stack justifyContent='space-between' sx={{ height: '100%' }}>
            <Toolbar>
              <Box sx={{ flexGrow: 1 }}>
                <Box
                  sx={{ display: { xs: 'none', lg: 'block' } }}
                  onClick={this.onLogoClick.bind(this)}
                >
                  <Logo className='klayo-appdrawer_logo' height='100%' />
                </Box>
              </Box>
              <Box>
                {user && (
                  <Button
                    className='klayo-appbar_notificationbutton klayo-appbar_button'
                    size='sm'
                    ariaLabel='Notifications'
                    icon={<BellIcon />}
                    badgeProps={{
                      badgeContent: unreadNotifications,
                      invisible: unreadNotifications === null,
                      variant: 'dot',
                      color: 'error',
                      max: 99,
                    }}
                    color='inherit'
                    tooltip={
                      'Unread notifications: ' + (unreadNotifications ? unreadNotifications : 0)
                    }
                    onClick={onShowNotifications}
                    sx={{ display: { xs: 'none', lg: 'flex' } }}
                  />
                )}
                <Button
                  className='klayo-appdrawer_closebutton'
                  autoFocus
                  variant='simple'
                  size='sm'
                  icon={<CloseIcon />}
                  onClick={this.onClose.bind(this)}
                  sx={{ display: { xs: 'block', lg: 'none' } }}
                />
              </Box>
            </Toolbar>

            <div className='klayo-appdrawer-menuwrapper' style={{ flexGrow: 1 }}>
              <AppNav
                ref={this.navRef}
                location={location}
                user={user}
                organization={organization}
                defaultItem={defaultNavItem}
                items={navItems}
                currentPage={currentPage}
                open={true}
                drawerMode={true}
                devMode={devMode}
                showIcons={false /*!isDesktop*/}
                allowSwitchUser={allowSwitchUser}
                allowSettings={allowSettings}
                getChildNavItems={getChildNavItems}
                onSwitchUser={this.onShowSwitchUserDialog.bind(this)}
                onDevOptions={this.onOpenDevDialog.bind(this)}
                onSettings={this.onOpenSettings.bind(this)}
                onSignOut={this.onSignOut.bind(this)}
                allowDemoSwitch={allowDemoSwitch}
                onDemoSwitch={onDemoSwitch}
                onNavigation={onNavigation}
                itemState={itemState}
                onDrawerClose={this.onClose.bind(this)}
              />

              {!isDesktop && <Divider />}

              <AppMenu
                organization={organization}
                open={!isDesktop || userButton !== null}
                drawerMode={!isDesktop}
                anchorEl={isDesktop ? userButton : null}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                devMode={devMode}
                showIcons={isDesktop}
                allowSwitchUser={allowSwitchUser}
                onClose={this.onUserMenuClose.bind(this)}
                onSwitchUser={this.onShowSwitchUserDialog.bind(this)}
                onDevOptions={this.onOpenDevDialog.bind(this)}
                onSignOut={this.onSignOut.bind(this)}
                allowDemoSwitch={allowDemoSwitch}
                onDemoSwitch={this.onDemoSwitch.bind(this)}
                allowDemoDataSwitch={allowDemoDataSwitch}
                onDemoDataSwitch={onDemoDataSwitch}
                onMyAccount={this.onMyAccount.bind(this)}
              />
            </div>

            {/* Hidden in the Try It Now sandbox: it navigates to /solution-finder, which is outside
                PREVIEW_NAV_SLUGS, so an armed session lands on a page whose real reads the
                fail-closed mock silently blocks — and since the demo holds a single history entry,
                going there destroys it with no way back. */}
            {!isPreviewArmed() && organization?.enabledFeatures?.MyKlayo !== false && (
              <ExploreKlayoTeaser />
            )}

            {isDesktop && (
              <div>
                <ListItem
                  className='klaro-appdrawer_usermenuitem'
                  onClick={this.onUserButton.bind(this)}
                >
                  <ListItemIcon>
                    <CurrentUserAvatar size='xs' user={user} />
                  </ListItemIcon>
                  <ListItemText
                    sx={{
                      '& .MuiListItemText-primary': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                      '& .MuiListItemText-secondary': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                    primary={
                      user ? (
                        <Tooltip title={user.fullName} placement='top'>
                          <span>{user.fullName}</span>
                        </Tooltip>
                      ) : (
                        ''
                      )
                    }
                    secondary={
                      organization ? (organization.isDemo ? 'Demo Company' : organization.name) : ''
                    }
                  />
                  <IconButton
                    size='small'
                    sx={{
                      '& svg path[fill="#1B3DF2"]': {
                        fill: KLAYO_COLORS.GreyGR400,
                      },

                      '&:hover svg path[fill="#1B3DF2"]': {
                        fill: KLAYO_COLORS.NeonBlueNB500,
                      },
                    }}
                  >
                    <MoreVerticalIcon />
                  </IconButton>
                </ListItem>
              </div>
            )}
          </Stack>
        </Box>
      </Drawer>
    );
  }
}
